import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, HandHeart, Users, Sparkles, CheckCircle2, Map } from "lucide-react";
import { useSeo } from "../lib/seo";

const services = [
  {
    id: "sil",
    icon: HomeIcon,
    title: "Supported Independent Living (SIL)",
    image: "https://images.pexels.com/photos/8543570/pexels-photo-8543570.jpeg",
    alt: "Support worker chatting warmly with a young adult participant at home",
    summary: "Daily, ongoing support to help you live independently in your own home — whether you live alone, with housemates, or with family. SIL is our heart-and-soul service: consistent workers, clear routines, real independence.",
    bullets: [
      "Around-the-clock or scheduled in-home support tailored to your plan",
      "Help with personal care, cooking, cleaning, medication and routines",
      "Skill-building so you can do more for yourself, year on year",
      "Shared-living and individual SIL arrangements supported",
      "Active overnight and sleepover support available",
    ],
    goal: "Helps participants live the way they want to live — at home, with the right support, on their own terms.",
  },
  {
    id: "inhome",
    icon: HandHeart,
    title: "In-Home Support & Personal Care",
    image: "https://customer-assets.emergentagent.com/job_valora-respite/artifacts/hdcwk5ad_in%20home.jpg",
    alt: "Young adult participant cooking in a bright kitchen with their support worker",
    summary: "Warm, respectful support right where you live — with the same workers you know and trust. From morning routines to evening wind-down, we fit around your day.",
    bullets: [
      "Showering, dressing, grooming and toileting with dignity",
      "Meal preparation, light household tasks and laundry",
      "Medication prompts and overnight support",
      "Companionship and emotional wellbeing check-ins",
    ],
    goal: "Helps participants live safely and confidently at home, with the supports they choose.",
  },
  {
    id: "community",
    icon: Users,
    title: "Community Access & Social Participation",
    image: "https://images.pexels.com/photos/8556492/pexels-photo-8556492.jpeg",
    alt: "Group of friends laughing and connecting outdoors",
    summary: "Getting out into the world — for fun, friendship, fitness, and the things that matter to you. Our workers help break down barriers so you can fully take part.",
    bullets: [
      "Cafés, parks, libraries, gyms, concerts, sports and clubs",
      "Travel training on public transport",
      "Group activities and one-to-one outings",
      "Faith, cultural and identity-affirming experiences",
    ],
    goal: "Builds confidence, friendships and a real sense of belonging in your community.",
  },
  {
    id: "capacity",
    icon: Sparkles,
    title: "Capacity Building & Life Skills",
    image: "https://images.unsplash.com/photo-1633466153506-a396b670565b",
    alt: "Smiling woman in a wheelchair carrying a box with confidence",
    summary: "Practical, hands-on skill building tied directly to the goals in your NDIS plan. We celebrate progress in everyday wins, not big leaps.",
    bullets: [
      "Cooking, budgeting, shopping and meal planning",
      "Tenancy support, paperwork and appointment prep",
      "Pre-employment skills, volunteering and study support",
      "Self-advocacy and decision-making support",
    ],
    goal: "Grows independence in a way that's measurable and meaningful to you.",
  },
  {
    id: "coordination",
    icon: Map,
    title: "Support Coordination",
    image: "https://customer-assets.emergentagent.com/job_valora-respite/artifacts/co052d4n_pp.jpg",
    alt: "Support coordinator helping a young adult participant review paperwork on a couch",
    summary: "We help you make the most of your NDIS plan — connecting you with the right providers, navigating paperwork, and keeping everything on track so you can focus on living.",
    bullets: [
      "Plan walkthrough and goal mapping",
      "Provider sourcing, comparisons and warm introductions",
      "Service agreement and booking management",
      "Crisis support and plan reviews when life changes",
    ],
    goal: "Turns your NDIS plan into real, coordinated, working supports — without the paperwork stress.",
  },
];

export default function Services() {
  useSeo({
    title: "NDIS Services & Supports | Valora Support Services",
    description: "Explore Valora's NDIS services across Australia: Supported Independent Living (SIL), in-home support, community access and capacity building.",
  });

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-24">
          <p className="eyebrow">Our services</p>
          <h1 className="mt-3 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#0F2A2C]">
            Led by Supported Independent Living — built around your life.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#4A5568]">
            SIL is our flagship — daily support to help you live independently at home. Around it we wrap in-home support, community access and capacity building, all delivered with the same warmth and consistency.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {services.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="pill hover:bg-[#E6FFFA] transition" data-testid={`service-anchor-${s.id}`}>
                <s.icon className="h-3.5 w-3.5" /> {s.title.split(" ")[0]}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="section-y bg-white">
        <div className="container-x space-y-20 lg:space-y-28">
          {services.map((s, i) => (
            <article id={s.id} key={s.id} data-testid={`service-block-${s.id}`} className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <div className="lg:col-span-6">
                <div className="rounded-[28px] overflow-hidden aspect-[5/4] shadow-[0_30px_80px_-40px_rgba(11,110,112,0.4)]">
                  <img src={s.image} alt={s.alt} className="h-full w-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2.5 mb-4">
                  <span className="h-11 w-11 rounded-xl bg-[#E6FFFA] text-[#0B6E70] flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="eyebrow">Service 0{i + 1}</span>
                </div>
                <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[#0F2A2C] tracking-tight">{s.title}</h2>
                <p className="mt-4 text-[#4A5568] text-lg leading-relaxed">{s.summary}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#0F2A2C]">
                      <CheckCircle2 className="h-5 w-5 text-[#0B6E70] flex-shrink-0 mt-0.5" />
                      <span className="text-[15px]">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 rounded-2xl bg-[#F8FBFA] border border-[#e6efee] p-5">
                  <p className="text-sm uppercase tracking-wider font-semibold text-[#827397]">How it helps</p>
                  <p className="mt-1.5 text-[#0F2A2C]">{s.goal}</p>
                </div>
                <Link to="/referrals" data-testid={`service-enquire-${s.id}`} className="mt-7 btn-primary">
                  Enquire about {s.title.split("(")[0].split("&")[0].trim().toLowerCase()} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA removed */}
    </div>
  );
}
